import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  onSnapshot,
  where,
  orderBy,
  getDocs,
  DocumentReference,
  CollectionReference,
} from 'firebase/firestore';
import { db } from '@/firebase';
import type { Appliance, ServiceRecord, ServiceRecordType, ApplianceRecall, RiskScore } from '@/types/index';
import { useAiAction } from '@/hooks/useAiAction';

interface ApplianceContextType {
  appliances: Appliance[];
  loading: boolean;
  householdId: string | null;
  addAppliance: (appliance: Omit<Appliance, 'id' | 'createdAt'>) => Promise<string>;
  updateAppliance: (id: string, updates: Partial<Appliance>) => Promise<void>;
  deleteAppliance: (id: string) => Promise<void>;
  addServiceRecord: (applianceId: string, record: Omit<ServiceRecord, 'id' | 'createdAt'>) => Promise<string>;
  getServiceRecords: (applianceId: string) => Promise<ServiceRecord[]>;
  getRecalls: (applianceId: string) => Promise<ApplianceRecall[]>;
  predictMaintenance: (
    appliance: Appliance,
    serviceHistory: ServiceRecord[]
  ) => Promise<{ predictedNextServiceDate: string; riskScore: RiskScore; reasoning: string } | null>;
}

const ApplianceContext = createContext<ApplianceContextType | undefined>(undefined);

export const ApplianceProvider: React.FC<{ householdId: string | null; children: React.ReactNode }> = ({
  householdId,
  children,
}) => {
  const [appliances, setAppliances] = useState<Appliance[]>([]);
  const [loading, setLoading] = useState(true);
  const { callAction } = useAiAction(householdId);

  useEffect(() => {
    if (!householdId) {
      setAppliances([]);
      setLoading(false);
      return;
    }

    const appliancesRef = collection(db, 'households', householdId, 'appliances');
    const q = query(appliancesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const appliancesList: Appliance[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        appliancesList.push({
          id: docSnap.id,
          householdId,
          name: data.name || '',
          addedBy: data.addedBy || '',
          serialNumber: data.serialNumber || undefined,
          room: data.room || undefined,
          warrantyExpiresAt: data.warrantyExpiresAt ? new Date(data.warrantyExpiresAt).toISOString() : undefined,
          notes: data.notes || undefined,
          createdAt: data.createdAt ? new Date(data.createdAt).toISOString() : new Date().toISOString(),
          make: data.make || '',
          model: data.model || '',
          modelNumber: data.modelNumber || undefined,
          purchaseDate: data.purchaseDate ? new Date(data.purchaseDate).toISOString() : undefined,
          warrantyExpiresDate: data.warrantyExpiresDate ? new Date(data.warrantyExpiresDate).toISOString() : undefined,
          manualUrl: data.manualUrl || undefined,
          photoUrl: data.photoUrl || undefined,
          category: data.category || undefined,
          recallStatus: data.recallStatus || undefined,
          totalServiceCost: data.totalServiceCost ?? 0,
          purchasePrice: data.purchasePrice ?? undefined,
        });
      });
      setAppliances(appliancesList);
      setLoading(false);
    }, (err) => {
      console.error('Failed to load appliances:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [householdId]);

  const addAppliance = useCallback(async (appliance: Omit<Appliance, 'id' | 'createdAt'>): Promise<string> => {
    if (!householdId) throw new Error('householdId is required');

    const now = new Date().toISOString();
    const applianceRef = collection(db, 'households', householdId, 'appliances');
    const docRef = await addDoc(applianceRef, {
      ...appliance,
      createdAt: now,
      warrantyExpiresAt: appliance.warrantyExpiresDate || undefined,
      purchaseDate: appliance.purchaseDate || undefined,
    });

    return docRef.id;
  }, [householdId]);

  const updateAppliance = useCallback(
    async (id: string, updates: Partial<Appliance>): Promise<void> => {
      if (!householdId) throw new Error('householdId is required');

      const applianceRef = doc(db, 'households', householdId, 'appliances', id);
      const updatesToWrite: Partial<Appliance> = {
        ...updates,
        warrantyExpiresDate: updates.warrantyExpiresAt
          ? new Date(updates.warrantyExpiresAt).toISOString()
          : undefined,
        purchaseDate: updates.purchaseDate ? new Date(updates.purchaseDate).toISOString() : undefined,
      };
      delete updatesToWrite.warrantyExpiresAt;
      delete updatesToWrite.purchaseDate;

      await updateDoc(applianceRef, updatesToWrite);
    },
    [householdId]
  );

  const deleteAppliance = useCallback(
    async (id: string): Promise<void> => {
      if (!householdId) throw new Error('householdId is required');

      const applianceRef = doc(db, 'households', householdId, 'appliances', id);
      await deleteDoc(applianceRef);
    },
    [householdId]
  );

  const addServiceRecord = useCallback(
    async (applianceId: string, record: Omit<ServiceRecord, 'id' | 'createdAt'>): Promise<string> => {
      if (!householdId) throw new Error('householdId is required');

      const serviceRecordsRef = collection(db, 'households', householdId, 'appliances', applianceId, 'serviceRecords');
      const now = new Date().toISOString();
      const docRef = await addDoc(serviceRecordsRef, {
        ...record,
        createdAt: now,
      });

      return docRef.id;
    },
    [householdId]
  );

  const getServiceRecords = useCallback(
    async (applianceId: string): Promise<ServiceRecord[]> => {
      if (!householdId) return [];

      const recordsRef = collection(db, 'households', householdId, 'appliances', applianceId, 'serviceRecords');
      const q = query(recordsRef, orderBy('date', 'desc'));

      const snapshot = await getDocs(q);
      return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          date: data.date ? new Date(data.date).toISOString() : '',
          cost: data.cost ?? 0,
          contractor: data.contractor || '',
          notes: data.notes || '',
          receiptPhotoUrl: data.receiptPhotoUrl || undefined,
          type: data.type || ('repair' as ServiceRecordType),
          createdAt: data.createdAt || '',
        };
      });
    },
    [householdId]
  );

  const getRecalls = useCallback(
    async (applianceId: string): Promise<ApplianceRecall[]> => {
      if (!householdId) return [];

      const recallsRef = collection(db, 'households', householdId, 'appliances', applianceId, 'recalls');
      const q = query(recallsRef, orderBy('detectedAt', 'desc'));

      const snapshot = await getDocs(q);
      return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          recallId: data.recallId || '',
          title: data.title || '',
          description: data.description || '',
          hazard: data.hazard || '',
          remedy: data.remedy || '',
          url: data.url || '',
          detectedAt: data.detectedAt || '',
        };
      });
    },
    [householdId]
  );

  const predictMaintenance = useCallback(
    async (appliance: Appliance, serviceHistory: ServiceRecord[]): Promise<{ predictedNextServiceDate: string; riskScore: RiskScore; reasoning: string } | null> => {
      if (!householdId || !callAction) return null;

      if (!appliance.category || !appliance.purchaseDate) return null;

      const last10Records = serviceHistory.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 10);
      const lastServiceDate = last10Records.length > 0 ? last10Records[0].date : null;

      const payload = {
        category: appliance.category,
        purchaseDate: appliance.purchaseDate,
        lastServiceDate,
        serviceHistory: last10Records.map(r => ({
          date: r.date,
          cost: r.cost,
          type: r.type,
          notes: r.notes,
        })),
      };

      try {
        const result = await callAction<{ predictedNextServiceDate: string; riskScore: RiskScore; reasoning: string }>(
          'predict_appliance_service',
          payload
        );
        return result ?? null;
      } catch (err) {
        console.error('predictMaintenance error:', err);
        return null;
      }
    },
    [householdId, callAction]
  );

  const value = useMemo(
    () => ({
      appliances,
      loading,
      householdId,
      addAppliance,
      updateAppliance,
      deleteAppliance,
      addServiceRecord,
      getServiceRecords,
      getRecalls,
      predictMaintenance,
    }),
    [
      appliances,
      loading,
      householdId,
      addAppliance,
      updateAppliance,
      deleteAppliance,
      addServiceRecord,
      getServiceRecords,
      getRecalls,
      predictMaintenance,
    ]
  );

  return <ApplianceContext.Provider value={value}>{children}</ApplianceContext.Provider>;
};

export const useAppliances = (): ApplianceContextType => {
  const context = useContext(ApplianceContext);
  if (context === undefined) {
    throw new Error('useAppliances must be used within an ApplianceProvider');
  }
  return context;
};
